import React from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { AuthPermission, AuthPermissions } from 'skipify-types';

import { tabs, configurationPaths, paylinkPaths, cardOnFilePaths } from './util/constants';
import AppShell from './AppShell';
import { PaymentsAndProcessing } from './pages/PaymentsAndProcessing';
import { MerchantDomains } from './pages/MerchantDomains';
import { Branding } from './pages/Branding';
import Fulfillment from './pages/Fulfillment';
import { MerchantWebhooks } from './pages/MerchantWebhooks/MerchantWebhooks';
import { Tipping } from './pages/Tipping';
import { CartEdits } from './pages/CartEdits';
import { PaylinksAppShell } from './pages/Paylinks/PaylinksAppShell';
import SendPaylinks from './pages/Paylinks/SendPaylinks';
import Customers from './pages/Paylinks/Customers';
import { ViewAllPaylinks } from './pages/Paylinks/ViewAll';
import {
  AGENT_HUB_PERMISSIONS,
  CARD_ON_FILE_PERMISSIONS,
  INSIGHTS_PERMISSIONS,
  MERCHANT_PERMISSIONS,
  MerchantProvider,
  PAYLINK_PERMISSIONS,
} from './contexts/MerchantContext';
import { ShopperConsent } from './pages/ShopperConsent';
import { Fees } from './pages/Fees';
import ApiKeys from './pages/ApiKeys';
import { Configuration } from './pages/TabDefault/Configuration';
import MainLayout from './layouts/MainLayout';
import { CreateChildMerchant } from './pages/CreateChildMerchant';
import { CreateMerchant } from './pages/CreateMerchant';
import ShopifyProvisioning from './pages/ShopifyProvisioning';
import ShopifyProvisioningDetail from './pages/ShopifyProvisioning/ShopifyProvisioningDetail';
import { PaylinkConfigurations } from './pages/PaylinkConfigurations';
import { ClickToPay } from './pages/ClickToPay';
import { FlexPay } from './pages/FlexPay';
import { AgenticCheckout } from './pages/AgenticCheckout';
import { AgentHubAppShell } from './pages/Ella/AgentHubAppShell';
import { EllaOnboarding } from './pages/Ella/onboarding';
import { AgentHub } from './pages/Ella/AgentHub';
import { DashboardTab } from './pages/Ella/tabs/DashboardTab';
import { AgentsTab } from './pages/Ella/tabs/AgentsTab';
import { CatalogTab } from './pages/Ella/tabs/CatalogTab';
import {
  ProvisioningBigCommercePage,
  ProvisioningIndexRedirect,
  ProvisioningShopifyPage,
  ProvisioningSquarespacePage,
} from './pages/Ella/provisioning';
import { CheckoutModeForm } from './pages/Ella/components/CheckoutModeForm';
import { EngagementForm } from './pages/Ella/components/EngagementForm';
import { EscalationForm } from './pages/Ella/components/EscalationForm';
import { SupportForm } from './pages/Ella/components/SupportForm';
import { PromptManagementForm } from './pages/Ella/components/PromptManagementForm';
import { WorkflowsForm } from './pages/Ella/components/WorkflowsForm';
import { AuditLogs } from './pages/AuditLogs';
import { MerchantUsers } from './pages/MerchantUsers';
import { PortalUsers } from './pages/PortalUsers';
import { PortalUserRoles } from './pages/PortalUsers/PortalUserRoles';
import { PortalUserAuditLogs } from './pages/PortalUsers/PortalUserAuditLogs';
import {
  CardOnFileAppShell,
  CreateTransaction,
  ViewAllTransactions,
  Customers as CardOnFileCustomers,
} from './pages/CardOnFile';
import { AuthenticatedRouteGuard } from './components/AuthenticatedRouteGuard';
import { MerchantInsights } from './pages/MerchantInsights/MerchantInsights';

/**
 * Route objects with routePermissions will be wrapped in an AuthenticatedRouteGuard component
 * that will check if the user has the required permissions to access the route
 *
 * If no routePermissions are provided, the route will not be wrapped in an AuthenticatedRouteGuard
 * component and will be accessible to all users
 */
type RouteObjectWithPermissions = RouteObject & {
  routePermissions?: readonly AuthPermission[];
  handle?: {
    title?: string;
  };
};

const addPermissionsGuard = ({
  routePermissions,
  ...route
}: RouteObjectWithPermissions): RouteObject => {
  if (!routePermissions) return route;

  return {
    ...route,
    element: route.element ? (
      <AuthenticatedRouteGuard routePermissions={routePermissions}>
        {route.element}
      </AuthenticatedRouteGuard>
    ) : (
      <AuthenticatedRouteGuard routePermissions={routePermissions} />
    ),
  };
};

const merchantConfigRoutes: RouteObjectWithPermissions[] = [
  { index: true, element: <Configuration />, handle: { title: 'Overview' } },
  {
    path: configurationPaths.PAYMENTS_AND_PROCESSING,
    element: <PaymentsAndProcessing />,
    handle: { title: 'Payments & Processing' },
  },
  {
    path: configurationPaths.MERCHANT_DOMAINS,
    element: <MerchantDomains />,
    handle: { title: 'Merchant Domains' },
  },
  { path: configurationPaths.BRANDING, element: <Branding />, handle: { title: 'Branding' } },
  {
    path: configurationPaths.FULFILLMENT,
    element: <Fulfillment />,
    handle: { title: 'Fulfillment' },
  },
  {
    path: configurationPaths.MERCHANT_WEBHOOKS,
    element: <MerchantWebhooks />,
    handle: { title: 'Webhooks' },
  },
  {
    path: configurationPaths.PAYLINKS,
    element: <PaylinkConfigurations />,
    handle: { title: 'Paylinks Configurations' },
  },
  { path: configurationPaths.TIPPING, element: <Tipping />, handle: { title: 'Tipping' } },
  { path: configurationPaths.CART_EDITS, element: <CartEdits />, handle: { title: 'Cart Edits' } },
  {
    path: configurationPaths.SHOPPER_CONSENT,
    element: <ShopperConsent />,
    handle: { title: 'Shopper Consent' },
  },
  { path: configurationPaths.FEES, element: <Fees />, handle: { title: 'Fees & Discounts' } },
  { path: configurationPaths.API_KEYS, element: <ApiKeys />, handle: { title: 'API Keys' } },
  {
    path: configurationPaths.CREATE_CHILD_MERCHANT,
    element: <CreateChildMerchant />,
    routePermissions: [AuthPermissions.ADMIN_ALL, AuthPermissions.CREATE_MERCHANTS],
    handle: { title: 'Create Child Merchant' },
  },
  {
    path: configurationPaths.FLEX_PAY,
    element: <FlexPay />,
    handle: { title: 'Flex Pay' },
  },
  {
    path: configurationPaths.CLICK_TO_PAY,
    element: <ClickToPay />,
    routePermissions: [AuthPermissions.ADMIN_ALL],
    handle: { title: 'Click to Pay' },
  },
  {
    path: configurationPaths.AGENTIC_CHECKOUT,
    element: <AgenticCheckout />,
    routePermissions: [AuthPermissions.ADMIN_ALL],
    handle: { title: 'Agentic Checkout' },
  },
  {
    path: configurationPaths.AUDIT_LOGS,
    element: <AuditLogs />,
    routePermissions: [AuthPermissions.ADMIN_ALL],
    handle: { title: 'Audit Logs' },
  },
  {
    path: configurationPaths.MERCHANT_USERS,
    element: <MerchantUsers />,
    routePermissions: [AuthPermissions.ADMIN_ALL, AuthPermissions.MANAGE_PORTAL_USERS],
    handle: { title: 'Users' },
  },
];

const paylinkRoutes: RouteObjectWithPermissions[] = [
  {
    index: true,
    element: <SendPaylinks />,
    routePermissions: [AuthPermissions.CREATE_PAYLINKS, AuthPermissions.CREATE_OWN_PAYLINKS],
    handle: { title: 'Send Paylinks' },
  },
  {
    path: paylinkPaths.VIEW_ALL,
    element: <ViewAllPaylinks />,
    routePermissions: [AuthPermissions.READ_PAYLINKS, AuthPermissions.READ_OWN_PAYLINKS],
    handle: { title: 'View All Paylinks' },
  },
  {
    path: paylinkPaths.CUSTOMERS,
    element: <Customers />,
    routePermissions: [
      AuthPermissions.READ_PAYLINK_CUSTOMERS,
      AuthPermissions.READ_OWN_PAYLINK_CUSTOMERS,
      AuthPermissions.CREATE_PAYLINK_CUSTOMERS,
    ],
    handle: { title: 'Customers' },
  },
];

const cardOnFileRoutes: RouteObjectWithPermissions[] = [
  {
    index: true,
    element: <CreateTransaction />,
    routePermissions: [AuthPermissions.CHARGE_MIT, AuthPermissions.CHARGE_OWN_MIT],
    handle: { title: 'Create Transaction' },
  },
  {
    path: cardOnFilePaths.VIEW_ALL,
    element: <ViewAllTransactions />,
    routePermissions: [AuthPermissions.READ_MIT, AuthPermissions.READ_OWN_MIT],
    handle: { title: 'Transactions' },
  },
  {
    path: cardOnFilePaths.CUSTOMERS,
    element: <CardOnFileCustomers />,
    routePermissions: [
      AuthPermissions.READ_PAYLINK_CUSTOMERS,
      AuthPermissions.READ_OWN_PAYLINK_CUSTOMERS,
    ],
    handle: { title: 'Customers' },
  },
];

const insightsRoutes: RouteObjectWithPermissions[] = [
  {
    index: true,
    element: <MerchantInsights />,
    routePermissions: [AuthPermissions.READ_MERCHANT_INSIGHTS],
    handle: { title: 'Merchant Insights' },
  },
];

const ellaRoutes: RouteObjectWithPermissions[] = [
  {
    path: 'onboarding',
    element: <EllaOnboarding />,
    handle: { title: 'Enable Agent Hub' },
  },
  {
    element: <AgentHub />,
    children: [
      {
        path: 'dashboard',
        element: <DashboardTab />,
        handle: { title: 'Dashboard' },
      },
      {
        path: 'agents',
        element: <AgentsTab />,
        handle: { title: 'Agents' },
      },
      {
        path: 'prompt-management',
        element: <PromptManagementForm />,
        handle: { title: 'Rules' },
      },
      {
        path: 'engagement',
        element: <EngagementForm />,
        handle: { title: 'Engagement' },
      },
      {
        path: 'escalation',
        element: <EscalationForm />,
        handle: { title: 'Escalation' },
      },
      {
        path: 'support',
        element: <SupportForm />,
        handle: { title: 'Support' },
      },
      {
        path: 'checkout-mode',
        element: <CheckoutModeForm />,
        handle: { title: 'Checkout Mode' },
      },
      {
        path: 'workflows',
        element: <WorkflowsForm />,
        handle: { title: 'Workflows' },
      },
      {
        path: 'catalog',
        element: <CatalogTab />,
        handle: { title: 'Catalog' },
      },
      {
        path: 'provisioning',
        children: [
          {
            index: true,
            element: <ProvisioningIndexRedirect />,
          },
          {
            path: 'shopify',
            element: <ProvisioningShopifyPage />,
            handle: { title: 'Provisioning' },
          },
          {
            path: 'bigcommerce',
            element: <ProvisioningBigCommercePage />,
            handle: { title: 'Provisioning' },
          },
          {
            path: 'squarespace',
            element: <ProvisioningSquarespacePage />,
            handle: { title: 'Provisioning' },
          },
        ],
      },
    ],
  },
];

const mainPages: RouteObjectWithPermissions[] = [
  {
    path: configurationPaths.PORTAL_USERS,
    element: <AppShell />,
    routePermissions: [AuthPermissions.ADMIN_ALL, AuthPermissions.MANAGE_PORTAL_USERS],
    children: [
      {
        index: true,
        element: <PortalUsers />,
        handle: { title: 'Manage Users' },
      },
      {
        path: configurationPaths.PORTAL_USERS_ROLES,
        element: <PortalUserRoles />,
        handle: { title: 'Roles & Permissions' },
      },
      {
        path: configurationPaths.PORTAL_USERS_AUDIT_LOGS,
        element: <PortalUserAuditLogs />,
        handle: { title: 'User Audit Logs' },
      },
    ].map(addPermissionsGuard),
  },
  {
    path: configurationPaths.ADMIN,
    element: <AppShell />,
    routePermissions: [AuthPermissions.ADMIN_ALL],
    children: [
      {
        index: true,
        element: <Navigate to={configurationPaths.ADMIN_CREATE_MERCHANT} replace />,
      },
      {
        path: configurationPaths.ADMIN_CREATE_MERCHANT,
        element: <CreateMerchant />,
        handle: { title: 'Create Merchant' },
      },
      {
        path: configurationPaths.ADMIN_SHOPIFY_PROVISIONING,
        element: <ShopifyProvisioning />,
        handle: { title: 'Shopify provisioning' },
      },
      {
        path: configurationPaths.ADMIN_SHOPIFY_PROVISIONING_DETAIL,
        element: <ShopifyProvisioningDetail />,
        handle: { title: 'Shopify provisioning' },
      },
    ].map(addPermissionsGuard),
  },
  {
    path: `/${tabs.CONFIGURATION}`,
    element: <AppShell />,
    routePermissions: MERCHANT_PERMISSIONS,
    children: merchantConfigRoutes.map(addPermissionsGuard),
  },
  {
    path: `/${tabs.AGENT_HUB}`,
    element: <AgentHubAppShell />,
    routePermissions: AGENT_HUB_PERMISSIONS,
    children: ellaRoutes.map(addPermissionsGuard),
  },
  {
    path: `/${tabs.PAYLINKS}`,
    element: <PaylinksAppShell />,
    routePermissions: PAYLINK_PERMISSIONS,
    children: paylinkRoutes.map(addPermissionsGuard),
  },
  {
    path: `/${tabs.CARD_ON_FILE}`,
    element: <CardOnFileAppShell />,
    routePermissions: CARD_ON_FILE_PERMISSIONS,
    children: cardOnFileRoutes.map(addPermissionsGuard),
  },
  {
    path: `/${tabs.INSIGHTS}`,
    element: <AppShell />,
    routePermissions: INSIGHTS_PERMISSIONS,
    children: insightsRoutes.map(addPermissionsGuard),
  },
];

const routes: RouteObject[] = [
  {
    element: (
      <MerchantProvider>
        <Outlet />
      </MerchantProvider>
    ),
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            // This main RouteGuard is used to check if the user is authenticated and the merchant
            // is loaded. The route guard for the children pages is used to check if the user has
            // the required permissions to access the page.
            element: <AuthenticatedRouteGuard />,
            children: mainPages.map(addPermissionsGuard),
          },
        ],
      },
      {
        path: '*',
        element: <AuthenticatedRouteGuard isFallbackRoute />,
      },
    ],
  },
];

export default routes;
